import { Controller } from "@hotwired/stimulus"

// The contact-resolution answer is one list of choices: a likely contact, any
// other contact (its search opens under its row) or a new contact (its name
// field opens under its row). Picking a
// row shows the pane for it: the map form for an existing contact, the create
// form for a new one.
export default class extends Controller {
  static targets = ["mapPane", "createPane", "contactReference", "mapSubmit", "mapSubmitLabel", "markVerified", "markVerifiedCheckbox", "reactivate", "reactivateCheckbox", "search", "newName"]
  static values = {
    verifiedReferences: Array, // references of contacts that are already verified
    inactiveReferences: Array // references of contacts that are inactive
  }

  connect() {
    this.syncMapSubmit()
    this.syncMarkVerified()
  }

  get selectedReference() {
    return this.hasContactReferenceTarget ? this.contactReferenceTarget.value.trim() : ""
  }

  get reactivateChecked() {
    if (!this.hasReactivateTarget || !this.hasReactivateCheckboxTarget) return false
    return !this.reactivateTarget.hidden && this.reactivateCheckboxTarget.checked
  }

  pick(event) {
    const value = event.target.value
    const creating = value === "new"
    const searching = value === ""

    if (this.hasSearchTarget) this.searchTarget.hidden = !searching
    if (this.hasNewNameTarget) this.newNameTarget.hidden = !creating
    this.activate(creating ? "create" : "map")
    if (creating) return

    if (this.hasContactReferenceTarget) {
      this.contactReferenceTarget.value = value
      this.syncMapSubmit()
    }

    if (searching && this.hasSearchTarget) {
      const input = this.searchTarget.querySelector("input[type='text']")
      if (input) {
        input.value = ""
        window.requestAnimationFrame(() => input.focus())
      }
    }
  }

  // "Add a note" and "Add instructions" are buttons in the foot; each opens
  // its field in the same form and steps aside.
  reveal(event) {
    event.preventDefault()
    const button = event.currentTarget
    const slot = button.closest("form")?.querySelector(`[data-reveal-slot="${button.dataset.reveal}"]`)
    if (!slot) return

    slot.hidden = false
    button.hidden = true
    slot.querySelector("input, textarea")?.focus()
  }

  activate(mode) {
    const showMap = mode !== "create"
    if (this.hasMapPaneTarget) this.mapPaneTarget.hidden = !showMap
    if (this.hasCreatePaneTarget) this.createPaneTarget.hidden = showMap
  }

  syncMapSubmit() {
    if (!this.hasMapSubmitTarget || !this.hasContactReferenceTarget) return
    this.mapSubmitTarget.disabled = this.selectedReference === ""
    this.syncReactivate()
    this.syncMarkVerified()
  }

  // Offer reactivation only for a selected inactive contact; uncheck it when
  // hidden so it never submits for a contact that is already active.
  syncReactivate() {
    if (!this.hasReactivateTarget) return
    const reference = this.selectedReference
    const show = reference !== "" && this.inactiveReferencesValue.includes(reference)
    this.reactivateTarget.hidden = !show
    if (!show && this.hasReactivateCheckboxTarget) {
      this.reactivateCheckboxTarget.checked = false
    }
    this.syncMapSubmitLabel()
  }

  // Show "Mark as verified" only when a contact is selected and it is neither
  // already verified nor inactive — an inactive contact gets the reactivate
  // choice instead, since verifying it would revive it as a side effect.
  // Uncheck it when hidden so it never submits for one of those contacts.
  syncMarkVerified() {
    if (!this.hasMarkVerifiedTarget) return
    const reference = this.selectedReference
    const show = reference !== "" &&
      !this.verifiedReferencesValue.includes(reference) &&
      !this.inactiveReferencesValue.includes(reference)
    this.markVerifiedTarget.hidden = !show
    if (!show && this.hasMarkVerifiedCheckboxTarget) {
      this.markVerifiedCheckboxTarget.checked = false
    }
  }

  // Keep the submit button honest about which of the two actions will happen.
  syncMapSubmitLabel() {
    if (!this.hasMapSubmitLabelTarget) return
    this.mapSubmitLabelTarget.textContent = this.reactivateChecked ? "Reactivate contact" : "Map contact"
  }
}
